import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

import { Button } from './button.tsx';

// CB-CONV §12 crew part 4B: the roster's popover, as the operator's ShiftPopover and OfferPopover
// boards draw it (docs/designs/roster-page.md §2). A modal dialog over a darkened page, the full
// screen below 640 px: a head (what it is, its state, when and where), a body, and a foot of
// actions. Labelled by its title, focus held inside, Escape and the backdrop close it, and focus
// goes back to what opened it.

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Popover({
  title,
  subtitle,
  lead,
  tag,
  footer,
  onClose,
  children,
}: {
  title: string;
  /** When and where, under the title. */
  subtitle?: ReactNode;
  /** Before the title: a person's initials. */
  lead?: ReactNode;
  /** Beside the title: its state. */
  tag?: ReactNode;
  /** The actions, in the foot. */
  footer?: ReactNode;
  onClose: () => void;
  children?: ReactNode;
}) {
  const id = useId();
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    // The first thing to act on in the body; else the dialog itself, so Close isn't the first stop.
    const body = box.current?.querySelector('.popover-body');
    const first = body?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? box.current)?.focus();
    return () => opener?.focus();
  }, []);

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab' || !box.current) return;
    const all = [...box.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
    if (all.length === 0) return;
    const [first, last] = [all[0]!, all[all.length - 1]!];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <div
      className="popover-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        tabIndex={-1}
        className="popover"
        onKeyDown={onKeyDown}
      >
        <div className="popover-head">
          {lead}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id={id} className="font-heading text-xl font-extrabold">
                {title}
              </h2>
              {tag}
            </div>
            {subtitle ? <p className="mt-1 text-[13px] text-muted">{subtitle}</p> : null}
          </div>
          <Button variant="quiet" className="px-2.5! py-1.5! text-xs!" onClick={onClose}>
            Close
          </Button>
        </div>
        <div className="popover-body">{children}</div>
        {footer ? <div className="popover-foot">{footer}</div> : null}
      </section>
    </div>
  );
}
